// As chamadas do Estúdio ao servidor. Chaves de consulta: ['estudio', loja, ...].
// Nenhuma regra da Meta mora aqui: proporção, formato e limite o servidor
// confere e devolve com a mensagem pronta.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { api } from '../../shared/api/api';
import { useLoja } from '../../shared/estado/loja';
import { registrarFontesDaLoja } from '../design/arte/fontes';
import type {
  AnaliseDaLoja, ArtesDoConteudo, ConteudoComIa, EntrevistaDaLoja, FluxoDasArtes, ListaDeInspiracoes, UmaInspiracao, EstadoDoInstagram, LegendaGerada, ListaDeConteudos, ListaDeIdeias, ListaDePosts, ListaDePublicacoes, LoteDeIdeias, MarcaDaLoja, PropostaDaMarca,
  ListaDeReferencias, PostsRecomendados, ReferenciaAnalisada, UmaIdeia, UmaPublicacao, UmaReferencia, UmConteudo,
  AmostraDaVoz, CatalogoDaMarca as CatalogoDaMarcaGerado, FluxoDoAvatar, FonteApagada, ListaDeFontesDaLoja, UmaFonteDaLoja,
} from '../../shared/api/tipos-gerados';

export type Post = ListaDePosts['posts'][number];
export type Publicacao = UmaPublicacao['publicacao'];

const base = () => `/api/tenant/${encodeURIComponent(useLoja.getState().tenantId ?? '')}/estudio/instagram`;
const useLojaAtual = () => useLoja((s) => s.tenantId) ?? '';

/**
 * De quanto em quanto o estado é relido: a cada 3 s enquanto o servidor busca no
 * Instagram, e nunca depois de uma leitura que falhou — o erro deixa o último
 * `atualizando: true` no cache, e relê-lo a cada 3 s com a API recusando (429)
 * só alimentava a recusa.
 */
export const releituraDoEstado = (s: { status: string; data?: Pick<EstadoDoInstagram, 'atualizando'> }) =>
  s.status !== 'error' && s.data?.atualizando ? 3000 : false;

/**
 * O estado da conexão. Ler o estado faz o servidor buscar no Instagram sozinho
 * quando a última coleta ficou velha (`atualizando`): enquanto isso, relê a
 * cada 3 s, e quando termina recarrega os posts — sem botão.
 */
export function useEstadoDoInstagram() {
  const loja = useLojaAtual();
  const consultas = useQueryClient();
  const estado = useQuery({
    queryKey: ['estudio', loja, 'instagram'], queryFn: () => api.get<EstadoDoInstagram>(base()), enabled: loja !== '',
    refetchInterval: (q) => releituraDoEstado(q.state),
  });
  const atualizando = !!estado.data?.atualizando;
  const antes = useRef(atualizando);
  useEffect(() => {
    if (antes.current && !atualizando) void consultas.invalidateQueries({ queryKey: ['estudio', loja, 'posts'] });
    antes.current = atualizando;
  }, [atualizando, consultas, loja]);
  return estado;
}

export function usePosts() {
  const loja = useLojaAtual();
  return useQuery({ queryKey: ['estudio', loja, 'posts'], queryFn: () => api.get<ListaDePosts>(`${base()}/posts?limite=100`).then((r) => r.posts), enabled: loja !== '' });
}

/** Enquanto houver publicação agendada ou saindo, a lista se atualiza sozinha a cada 30 s. */
export function usePublicacoes() {
  const loja = useLojaAtual();
  return useQuery({
    queryKey: ['estudio', loja, 'publicacoes'],
    queryFn: () => api.get<ListaDePublicacoes>(`${base()}/publicacoes?limite=50`).then((r) => r.publicacoes),
    enabled: loja !== '',
    refetchInterval: (q) => (q.state.data?.some((p) => p.status === 'agendada' || p.status === 'publicando') ? 30_000 : false),
  });
}

export function useRecarregarEstudio() {
  const consultas = useQueryClient();
  const loja = useLojaAtual();
  return (...parte: string[]) => consultas.invalidateQueries({ queryKey: ['estudio', loja, ...parte] });
}

export const publicar = (d: { ativos: string[]; legenda: string; agendarPara?: string | null; conteudoId?: string | null }) =>
  api.post<UmaPublicacao>(`${base()}/publicacoes`, d).then((r) => r.publicacao);
/** A IA olha as mídias escolhidas (e o que já está escrito) e devolve a legenda; não publica nada. */
export const gerarLegenda = (d: { ativos: string[]; rascunho: string }) => api.post<LegendaGerada>(`${base()}/legenda`, d);
export const cancelarPublicacao = (id: string) => api.delete<UmaPublicacao>(`${base()}/publicacoes/${id}`).then((r) => r.publicacao);

// ── Marca ──────────────────────────────────────────────────────────────────
export type Marca = MarcaDaLoja['marca'];
const baseDaMarca = () => `/api/tenant/${encodeURIComponent(useLoja.getState().tenantId ?? '')}/estudio/marca`;

export const chaveDaMarca = (loja: string) => ['estudio', loja, 'marca'] as const;
export function useMarca() {
  const loja = useLojaAtual();
  return useQuery({ queryKey: chaveDaMarca(loja), queryFn: () => api.get<MarcaDaLoja>(baseDaMarca()), enabled: loja !== '' });
}
export const salvarMarca = (m: Marca) => api.put<MarcaDaLoja>(baseDaMarca(), m);

// ── Fontes da loja (Marca) ──
/** Uma fonte que a loja enviou: o id ("loja-…") vai na Marca e nas camadas da arte; a url é servida na mesma origem. */
export type FonteDaLoja = ListaDeFontesDaLoja['fontes'][number];
export const chaveDasFontesDaLoja = (loja: string) => ['estudio', loja, 'fontes'] as const;
/** As fontes da loja — e já registradas no editor de arte (fonteDe, carregarFonte). */
export function useFontesDaLoja() {
  const loja = useLojaAtual();
  return useQuery({
    queryKey: chaveDasFontesDaLoja(loja), enabled: loja !== '',
    queryFn: () => api.get<ListaDeFontesDaLoja>(`${baseDaMarca()}/fontes`).then((r) => { registrarFontesDaLoja(r.fontes); return r.fontes; }),
  });
}
export const enviarFonte = (dataUrl: string, nome: string) => api.post<UmaFonteDaLoja>(`${baseDaMarca()}/fontes`, { dataUrl, nome }).then((r) => r.fonte);
export const apagarFonte = (id: string) => api.delete<FonteApagada>(`${baseDaMarca()}/fontes/${encodeURIComponent(id)}`);
export const sugerirMarca = (m: Marca) => api.post<PropostaDaMarca>(`${baseDaMarca()}/sugerir`, m);

/** Os estilos, os apresentadores e as vozes que a Marca oferece (e a foto do avatar da loja, assinada). */
export type CatalogoDaMarca = CatalogoDaMarcaGerado;
export function useCatalogoDaMarca() {
  const loja = useLojaAtual();
  return useQuery({ queryKey: ['estudio', loja, 'marca', 'catalogo'], queryFn: () => api.get<CatalogoDaMarca>(`${baseDaMarca()}/catalogo`), enabled: loja !== '', staleTime: 5 * 60_000 });
}
/** A frase de amostra na voz, em data URL (o "Ouvir"). */
export const ouvirVoz = (voz: string) => api.get<AmostraDaVoz>(`${baseDaMarca()}/vozes/${encodeURIComponent(voz)}`).then((r) => r.audio);
/** Monta no Design o fluxo que cria a foto do avatar; devolve o custo para a loja confirmar. */
export const montarAvatar = (descricao: string) => api.post<FluxoDoAvatar>(`${baseDaMarca()}/avatar`, { descricao });
/** A foto de cada apresentador da casa: estática, servida com o painel. */
export const fotoDoApresentador = (id: string) => `${import.meta.env.BASE_URL}estudio/apresentadores/${id}.webp`;
/** A miniatura de cada estilo, na mesma pasta estática. */
export const miniaturaDoEstilo = (tipo: 'imagem' | 'design', id: string) => `${import.meta.env.BASE_URL}estudio/estilos/${tipo}-${id}.webp`;

// ── Ideias e Conteúdos ─────────────────────────────────────────────────────
export type Ideia = ListaDeIdeias['ideias'][number];
export type Conteudo = UmConteudo['conteudo'];
const raiz = () => `/api/tenant/${encodeURIComponent(useLoja.getState().tenantId ?? '')}/estudio`;

export function useIdeias() {
  const loja = useLojaAtual();
  return useQuery({ queryKey: ['estudio', loja, 'ideias'], queryFn: () => api.get<ListaDeIdeias>(`${raiz()}/ideias`).then((r) => r.ideias), enabled: loja !== '' });
}
export function useConteudos() {
  const loja = useLojaAtual();
  return useQuery({ queryKey: ['estudio', loja, 'conteudos'], queryFn: () => api.get<ListaDeConteudos>(`${raiz()}/conteudos`).then((r) => r.conteudos), enabled: loja !== '' });
}
export function useConteudo(id: string | undefined) {
  const loja = useLojaAtual();
  return useQuery({
    queryKey: ['estudio', loja, 'conteudo', id], queryFn: () => api.get<UmConteudo>(`${raiz()}/conteudos/${id}`).then((r) => r.conteudo),
    enabled: loja !== '' && !!id, refetchOnWindowFocus: false,
  });
}
export const gerarIdeias = (d: { quantidade?: number; foco?: string | null }) => api.post<LoteDeIdeias>(`${raiz()}/ideias/gerar`, d);
export const dispensarIdeia = (id: string) => api.post<UmaIdeia>(`${raiz()}/ideias/${id}/dispensar`, {}).then((r) => r.ideia);
export const usarIdeia = (id: string, dataPrevista: string | null = null) => api.post<UmConteudo>(`${raiz()}/ideias/${id}/usar`, { dataPrevista }).then((r) => r.conteudo);
export const criarConteudo = (d: { titulo: string; formato?: Conteudo['formato']; funil?: Conteudo['funil'] }) => api.post<UmConteudo>(`${raiz()}/conteudos`, d).then((r) => r.conteudo);
export const mudarConteudo = (id: string, d: Partial<Pick<Conteudo, 'titulo' | 'formato' | 'funil' | 'pilar' | 'etapa' | 'dataPrevista' | 'slides' | 'legenda' | 'referenciaId'>>) =>
  api.patch<UmConteudo>(`${raiz()}/conteudos/${id}`, d).then((r) => r.conteudo);
export const apagarConteudo = (id: string) => api.delete<{ ok: true }>(`${raiz()}/conteudos/${id}`);
export const gerarRoteiro = (id: string) => api.post<ConteudoComIa>(`${raiz()}/conteudos/${id}/roteiro`, {});
export const refinarRoteiro = (id: string, pedido: string) => api.post<ConteudoComIa>(`${raiz()}/conteudos/${id}/refinar`, { pedido });

// ── Artes: a ponte com o Design ────────────────────────────────────────────
export type Arte = ArtesDoConteudo['artes'][number];
/** As saídas do fluxo do conteúdo. Relê ao voltar para a aba (a loja roda o fluxo no Design). */
export function useArtes(conteudoId: string | undefined, comFluxo: boolean) {
  const loja = useLojaAtual();
  return useQuery({
    queryKey: ['estudio', loja, 'artes', conteudoId],
    queryFn: () => api.get<ArtesDoConteudo>(`${raiz()}/conteudos/${conteudoId}/artes`).then((r) => r.artes),
    enabled: loja !== '' && !!conteudoId && comFluxo,
  });
}
export const abrirNoDesign = (id: string) => api.post<FluxoDasArtes>(`${raiz()}/conteudos/${id}/design`, {});

// ── Estratégia: inspirações, entrevista e análise ─────────────────────────
export type Inspiracao = UmaInspiracao['inspiracao'];
export type Entrevista = EntrevistaDaLoja;
export type Analise = NonNullable<AnaliseDaLoja['analise']>;
export function useInspiracoes() {
  const loja = useLojaAtual();
  return useQuery({ queryKey: ['estudio', loja, 'inspiracoes'], queryFn: () => api.get<ListaDeInspiracoes>(`${raiz()}/inspiracoes`).then((r) => r.inspiracoes), enabled: loja !== '' });
}
export const adicionarInspiracao = (entrada: string, plataforma: Inspiracao['plataforma'] = 'instagram') =>
  api.post<UmaInspiracao>(`${raiz()}/inspiracoes`, { entrada, plataforma }).then((r) => r.inspiracao);
export const removerInspiracao = (id: string) => api.delete<{ ok: true }>(`${raiz()}/inspiracoes/${id}`);

export type PostRecomendado = PostsRecomendados['posts'][number];
/** Os posts recentes dos criadores do Instagram (Business Discovery); `disponivel` falso sem o token da plataforma. */
export function useRecomendados(ativo = true) {
  const loja = useLojaAtual();
  return useQuery({ queryKey: ['estudio', loja, 'recomendados'], queryFn: () => api.get<PostsRecomendados>(`${raiz()}/inspiracoes/recomendados`), enabled: loja !== '' && ativo });
}
/** O post recomendado vira referência salva (ou volta a que já existe com o mesmo link). */
export const salvarDoRecomendado = (inspiracaoId: string, postId: string) =>
  api.post<UmaReferencia>(`${raiz()}/referencias/do-recomendado/${encodeURIComponent(inspiracaoId)}/${encodeURIComponent(postId)}`, {}).then((r) => r.referencia);

export function useEntrevista() {
  const loja = useLojaAtual();
  return useQuery({ queryKey: ['estudio', loja, 'entrevista'], queryFn: () => api.get<EntrevistaDaLoja>(`${raiz()}/entrevista`), enabled: loja !== '' });
}
export const iniciarEntrevista = () => api.post<EntrevistaDaLoja>(`${raiz()}/entrevista/iniciar`, {});
export const responderEntrevista = (texto: string) => api.post<EntrevistaDaLoja>(`${raiz()}/entrevista/responder`, { texto });
export const refazerEntrevista = () => api.post<EntrevistaDaLoja>(`${raiz()}/entrevista/refazer`, {});

export function useAnalise() {
  const loja = useLojaAtual();
  return useQuery({ queryKey: ['estudio', loja, 'analise'], queryFn: () => api.get<AnaliseDaLoja>(`${raiz()}/analise`), enabled: loja !== '' });
}
export const gerarAnalise = () => api.post<AnaliseDaLoja>(`${raiz()}/analise`, {});
export const TOPICOS_DA_FICHA: { chave: keyof EntrevistaDaLoja['ficha']; titulo: string; dica: string }[] = [
  { chave: 'historia', titulo: 'História & origem', dica: 'Quem é você e como chegou aqui…' },
  { chave: 'meta90', titulo: 'Meta dos 90 dias', dica: 'O que precisa acontecer…' },
  { chave: 'conteudo', titulo: 'Conteúdo que funciona', dica: 'O que dá orgulho ou resultado…' },
  { chave: 'travas', titulo: 'Travas atuais', dica: 'O que te emperra hoje…' },
];

// ── Referências ────────────────────────────────────────────────────────────
export type Referencia = UmaReferencia['referencia'];
export function useReferencias() {
  const loja = useLojaAtual();
  return useQuery({ queryKey: ['estudio', loja, 'referencias'], queryFn: () => api.get<ListaDeReferencias>(`${raiz()}/referencias`).then((r) => r.referencias), enabled: loja !== '' });
}
export const criarReferencia = (d: { url: string; texto?: string; notas?: string }) => api.post<UmaReferencia>(`${raiz()}/referencias`, d).then((r) => r.referencia);
export const mudarReferencia = (id: string, d: { texto?: string; notas?: string }) => api.patch<UmaReferencia>(`${raiz()}/referencias/${id}`, d).then((r) => r.referencia);
export const apagarReferencia = (id: string) => api.delete<{ ok: true }>(`${raiz()}/referencias/${id}`);
export const analisarReferencia = (id: string) => api.post<ReferenciaAnalisada>(`${raiz()}/referencias/${id}/analisar`, {});
export const PLATAFORMA: Record<Referencia['plataforma'], string> = { instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube' };

export const FORMATO: Record<Conteudo['formato'], string> = { carrossel: 'Carrossel', imagem: 'Imagem', reels: 'Reels', story: 'Story' };
export const FUNIL: Record<Conteudo['funil'], string> = { topo: 'Topo · atrair', meio: 'Meio · mostrar', fundo: 'Fundo · vender' };
export const ORIGEM: Record<string, string> = { conversas: 'Das conversas', produtos: 'Dos produtos', resultados: 'Dos resultados', pilar: 'De um pilar' };
export const ETAPA: Record<Conteudo['etapa'], string> = { ideia: 'Ideia', rascunho: 'Rascunho', pronto: 'Pronto', publicado: 'Publicado' };
